import { AfterViewInit, Component, ElementRef, inject, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { GraphTrendIconComponent } from '../../../../../_shared/components/graphs/graph-trend-icon/graph-trend-icon.component';
import {
  MultiLineChartComponent,
  MultiLineChartDataPoint,
  MultiLineChartSeries,
  LegendItem
} from '../../../../../_shared/components/graphs/multi-line-chart/multi-line-chart.component';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { FilterService } from '../../../../../_shared/services';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { ModalService } from '../../../../../_shared/services/modals/modal.service';
import { DashboardService } from '../../../services/dashboard.service';
import { WidgetFilterData } from '../../../../../_shared/models/filters/widget-filter-data.model';
import { calculateChartYTicks } from '../../../../../_shared/helpers/chart-y-ticks.helper';
import { ComplianceModalComponent } from '../../modals/compliance-modal/compliance-modal.component';
import { AccountDateFilterRequest } from '../../../../../_shared/models/account-date-filter-request.model';
import { ComplianceService } from '../../../../compliance/_shared/services/compliance.service';
import { ComplianceKpiRequest } from '../../../../compliance/_shared/models/compliance-kpi.model';
import { buildComplianceChart, getComplianceGrouping } from '../../../helpers/compliance-chart.helper';
import { ComplianceTimeSeriesPoint } from '../../../models/dashboard.model';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-compliance-trend',
  standalone: true,
  imports: [CommonModule, GraphTrendIconComponent, MultiLineChartComponent, EmptyStateComponent, TranslocoPipe],
  templateUrl: './compliance-trend.component.html',
  styleUrl: './compliance-trend.component.scss'
})
export class ComplianceTrendComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly transloco: TranslocoService = inject(TranslocoService);
  private filterService = inject(FilterService);
  private dashboardService = inject(DashboardService);
  private complianceService = inject(ComplianceService);
  private errorHandlerService = inject(ErrorHandlerService);
  private modalService = inject(ModalService);
  private ngZone = inject(NgZone);

  chartData: MultiLineChartDataPoint[] = [];
  series: MultiLineChartSeries[] = [];
  legendItems: LegendItem[] = [];
  private readonly defaultYTicks = [100, 80, 60, 40, 20, 0];
  yTicks: number[] = this.defaultYTicks;
  private readonly minChartHeight = 145;
  private readonly legendHeight = 27;
  chartHeight = this.minChartHeight;

  @ViewChild('chartBody') private chartBody!: ElementRef<HTMLElement>;
  private resizeObserver?: ResizeObserver;

  title: string = 'maintenanceDashboard.charts.compliance';
  isLoading: boolean = true;
  totalValue: number = 0;
  percentageChange: number = 0;
  widgetFilter!: WidgetFilterData;
  private points: ComplianceTimeSeriesPoint[] = [];

  private cancelSubject: Subject<void> = new Subject<void>();

  constructor() {
    this.transloco.langChanges$.pipe(takeUntilDestroyed()).subscribe(() => this.buildChart());

    this.filterService.filterUpdated$.pipe(takeUntilDestroyed()).subscribe(async (filter) => {
      if (!filter || Object.values(filter).every(x => x === null)) return;

      this.widgetFilter = filter;
      await this.loadComplianceData();
    });
  }

  ngOnInit(): void {
    this.widgetFilter = this.filterService.filter;

    if (this.widgetFilter && !Object.values(this.widgetFilter).every(x => x === null)) {
      this.loadComplianceData();
    }
  }

  ngAfterViewInit(): void {
    this.resizeObserver = new ResizeObserver(entries => {
      const bodyHeight = entries[0].contentRect.height;
      const chartHeight = Math.max(this.minChartHeight, Math.floor(bodyHeight - this.legendHeight));
      if (chartHeight !== this.chartHeight) {
        this.ngZone.run(() => this.chartHeight = chartHeight);
      }
    });
    this.resizeObserver.observe(this.chartBody.nativeElement);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  private async loadComplianceData(): Promise<void> {
    this.isLoading = true;

    try {
      this.cancelSubject.next();

      const request: AccountDateFilterRequest = {
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        interval: this.widgetFilter.interval,
        accountIds: this.widgetFilter.accountIds
      };

      const statsRequest: ComplianceKpiRequest = {
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        previousTimeFrom: this.widgetFilter.previousCycleStartDate!,
        previousTimeTo: this.widgetFilter.previousCycleEndDate!,
        accountIds: this.widgetFilter.accountIds
      };

      const [response, statsResponse] = await Promise.all([
        this.dashboardService.getAssetComplianceTimeSeries(request, this.cancelSubject),
        this.complianceService.getCompliantKpiStatsAsync(statsRequest, this.cancelSubject)
      ]);

      this.points = response.data ?? [];
      this.buildChart();

      this.totalValue = statsResponse?.data?.totalValue ?? 0;
      this.percentageChange = statsResponse?.data?.percentageChange ?? 0;

      this.isLoading = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.points = [];
          this.buildChart();
          this.totalValue = 0;
          this.percentageChange = 0;
          this.isLoading = false;
        }
      });
    }
  }

  private buildChart(): void {
    if (!this.widgetFilter?.startDate || !this.widgetFilter?.endDate) return;

    const startDate = this.widgetFilter.startDate;
    const endDate = this.widgetFilter.endDate;
    const chart = buildComplianceChart(
      this.points,
      startDate,
      endDate,
      getComplianceGrouping(startDate, endDate, 7),
      (key) => this.transloco.translate(key)
    );

    this.chartData = chart.data;
    this.series = chart.series;
    this.legendItems = chart.legend;
    this.calculateYTicks(this.chartData);
  }

  private calculateYTicks(data: MultiLineChartDataPoint[]): void {
    if (!data || data.length === 0) {
      this.yTicks = this.defaultYTicks;
      return;
    }

    const allValues: number[] = [];

    data.forEach(point => {
      this.series.forEach(s => {
        const value = point[s.key];
        if (typeof value === 'number') {
          allValues.push(value);
        }
      });
    });

    this.yTicks = calculateChartYTicks(allValues, this.defaultYTicks);
  }

  formatPercentChange(): string {
    const sign = this.percentageChange > 0 ? '+' : '';
    return `${sign}${this.percentageChange.toLocaleString('default', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
  }

  formatValue(): string {
    return this.transloco.translate('maintenanceDashboard.charts.compliantAssets', { count: this.totalValue.toLocaleString('default') });
  }

  formatTooltipValue(value: number): string {
    return `${value.toLocaleString('default', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  }

  hasKpi(): boolean {
    return this.hasData() || this.totalValue !== 0 || this.percentageChange !== 0;
  }

  hasData(): boolean {
    if (!this.chartData || this.chartData.length === 0) return false;

    return this.chartData.some(item => {
      return this.series.some(s => {
        const value = Number(item[s.key]) || 0;
        return value > 0;
      });
    });
  }

  openModal(): void {
    this.modalService.open(ComplianceModalComponent, {
      size: 'xl',
      centered: true,
      windowClass: 'compliance-modal'
    });
  }
}